import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SalesHeader, Btn } from "@/components/sales/ui";

const CONF = { high: "text-emerald-300", remembered: "text-emerald-300", ai: "text-cyan-300", medium: "text-amber-300", low: "text-red-300" };
const PROVIDERS = ["Endole", "Apollo", "Cognism", "Lusha", "Other"];

export default function ImportWizard() {
  const [provider, setProvider] = useState("Endole");
  const [job, setJob] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("update");
  const [dry, setDry] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const wrap = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const flat = (m) => Object.fromEntries(Object.entries(m).map(([h, v]) => [h, v.field]));

  const upload = (file) => wrap(async () => {
    const buf = new Uint8Array(await file.arrayBuffer());
    let bin = "";
    for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
    const { data } = await api.post("/sales/import/preview", { filename: file.name, content_b64: btoa(bin), provider });
    setJob(data); setMapping(data.mapping); setDry(null); setStatus(null);
  });
  const aiMap = () => wrap(async () => { const { data } = await api.post(`/sales/import/${job.id}/ai-map`); setMapping(data.mapping); setDry(null); });
  const check = () => wrap(async () => setDry((await api.post(`/sales/import/${job.id}/dry-run`, { mapping: flat(mapping), duplicate_mode: mode })).data));
  const commit = () => wrap(async () => {
    await api.post(`/sales/import/${job.id}/commit`, { mapping: flat(mapping), duplicate_mode: mode });
    const poll = async () => { const { data } = await api.get(`/sales/import/${job.id}`); setStatus(data); if (data.status !== "done") setTimeout(poll, 1500); };
    poll();
  });
  const unclear = Object.values(mapping).filter((m) => ["low", "medium"].includes(m.confidence)).length;

  if (status) return (
    <div data-testid="import-status"><SalesHeader title="Importing leads" />
      {status.status !== "done" ? <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Working… {status.progress || 0} rows processed</p> : (
        <div className="rounded-2xl border bg-card/70 p-6" data-testid="import-summary">
          <p className="font-display text-2xl font-bold">Import complete</p>
          <p className="mt-2 text-sm">{status.result.created} new companies · {status.result.updated} updated · {status.result.skipped} skipped · {status.result.contacts} contacts added</p>
          <div className="mt-4 flex gap-2"><Link to="/sales/companies" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">View companies</Link><Btn onClick={() => { setJob(null); setStatus(null); }}>Import another</Btn></div>
        </div>)}
    </div>
  );

  return (
    <div data-testid="import-page">
      <SalesHeader title="Import leads" subtitle="Upload a CSV from Endole, Apollo, Cognism, Lusha or anywhere else. We'll match the columns for you." />
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border bg-card/70 p-5">
        <select value={provider} onChange={(e) => setProvider(e.target.value)} className="h-10 rounded-full border bg-card px-3 text-sm" data-testid="import-provider-select">{PROVIDERS.map((p) => <option key={p}>{p}</option>)}</select>
        <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Choose CSV
          <input type="file" accept=".csv,.txt,.tsv" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="import-file-input" />
        </label>
        {job && <span className="text-sm text-muted-foreground">{job.row_count} rows · {job.encoding}</span>}
      </div>
      {job && (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm">{unclear ? <span className="text-amber-300">{unclear} column(s) need checking</span> : "All columns mapped"}</p>
            {unclear > 0 && <Btn onClick={aiMap} disabled={busy} data-testid="import-ai-map-btn"><Sparkles className="h-4 w-4" /> Ask AI about unclear columns</Btn>}
          </div>
          <div className="overflow-x-auto rounded-2xl border" data-testid="import-mapping-table">
            <table className="w-full text-sm"><thead className="bg-white/[0.03] text-left text-xs uppercase text-muted-foreground"><tr><th className="p-3">Your column</th><th className="p-3">Example</th><th className="p-3">Goes into</th></tr></thead>
              <tbody>{job.headers.map((h) => (
                <tr key={h} className="border-t"><td className="p-3 font-medium">{h}</td><td className="max-w-[240px] truncate p-3 text-muted-foreground">{job.sample[0]?.[h]}</td>
                  <td className="p-3"><select value={mapping[h]?.field} onChange={(e) => { setMapping({ ...mapping, [h]: { field: e.target.value, confidence: "high" } }); setDry(null); }}
                    className={`h-9 rounded-md border bg-card px-2 ${CONF[mapping[h]?.confidence]}`} data-testid={`map-${h}`}>{job.fields.map((f) => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}</select></td></tr>
              ))}</tbody></table>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <select value={mode} onChange={(e) => { setMode(e.target.value); setDry(null); }} className="h-10 rounded-full border bg-card px-3 text-sm" data-testid="import-duplicate-mode"><option value="update">Duplicates: fill in missing details</option><option value="skip">Duplicates: skip</option></select>
            <Btn onClick={check} disabled={busy} data-testid="import-preview-btn">Preview changes</Btn>
            {dry && <Btn variant="cyan" onClick={commit} disabled={busy} data-testid="import-commit-btn">Import {dry.counts.create + dry.counts.update + dry.counts.merge} records</Btn>}
          </div>
          {dry && (
            <div className="mt-4 rounded-2xl border bg-card/70 p-5 text-sm" data-testid="import-dry-run">
              <p><b>{dry.counts.create}</b> new · <b>{dry.counts.update}</b> update existing · <b>{dry.counts.merge}</b> merged duplicates in file · <b>{dry.counts.skip}</b> skipped</p>
              <ul className="mt-3 space-y-1 text-xs text-muted-foreground">{dry.sample.map((r, i) => <li key={i}><span className="text-foreground">{r.name}</span> → {r.action}{r.reason ? ` (${r.reason})` : ""}</li>)}</ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
